import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageTitle, EmptyState } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Check, X, ShieldCheck, Users } from "lucide-react";
import { toast } from "sonner";

const PLAN_LABELS = { bulan1: "Paket 1 Bulan", bulan6: "Paket 6 Bulan", premium: "Paket Premium", none: "Belum Aktif" };
const STATUS_COLORS = {
  active: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
  pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/40",
  expired: "bg-red-500/20 text-red-400 border-red-500/40",
  none: "bg-neutral-500/20 text-neutral-400 border-neutral-500/40",
};

export default function PersetujuanPaket() {
  const [pending, setPending] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [manualPlan, setManualPlan] = useState({});
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [p, u] = await Promise.all([
        api.get("/plans/pending").then((r) => r.data).catch(() => []),
        api.get("/users").then((r) => r.data).catch(() => []),
      ]);
      setPending(p);
      setAllUsers(u.filter((x) => x.role === "Pria" || x.role === "Wanita"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (userId, planOverride) => {
    setBusy(userId);
    try {
      await api.post(`/plans/approve/${userId}`, { plan: planOverride || null });
      toast.success("Paket disetujui & aktif");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyetujui"); }
    finally { setBusy(""); }
  };
  const reject = async (userId) => {
    setBusy(userId);
    try {
      await api.post(`/plans/reject/${userId}`);
      toast.success("Permintaan ditolak");
      await load();
    } catch (e) { toast.error("Gagal menolak"); }
    finally { setBusy(""); }
  };
  const manualActivate = async (userId) => {
    const plan = manualPlan[userId];
    if (!plan) { toast.error("Pilih paket dulu"); return; }
    setBusy(userId);
    try {
      await api.post(`/plans/approve/${userId}`, { plan });
      toast.success(`${PLAN_LABELS[plan]} diaktifkan`);
      setManualPlan((p) => ({ ...p, [userId]: "" }));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mengaktifkan"); }
    finally { setBusy(""); }
  };

  return (
    <div>
      <PageTitle title="Persetujuan Paket" desc="Kelola permintaan paket dari pengguna & aktifkan manual." icon={ShieldCheck} />

      {/* Pending requests */}
      <Card className="mb-6 p-6" data-testid="pending-plan-requests">
        <p className="mb-4 flex items-center gap-2 font-heading text-lg"><Users className="h-4 w-4" /> Permintaan Menunggu Persetujuan ({pending.length})</p>
        {loading ? <p className="text-sm text-muted-foreground">Memuat...</p> :
          pending.length === 0 ? <EmptyState label="Belum ada permintaan paket." /> :
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Username</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Paket Diminta</TableHead>
                  <TableHead>Diajukan</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pending.map((u) => (
                  <TableRow key={u.id} data-testid={`pending-row-${u.id}`}>
                    <TableCell className="font-medium">{u.username}</TableCell>
                    <TableCell className="text-muted-foreground">{u.email}</TableCell>
                    <TableCell>
                      <Select value={overrides[u.id] || u.plan_requested || "premium"} onValueChange={(v) => setOverrides((p) => ({ ...p, [u.id]: v }))}>
                        <SelectTrigger data-testid={`pending-plan-${u.id}`} className="h-8 w-36 rounded-full text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="bulan1">Paket 1 Bulan</SelectItem>
                          <SelectItem value="bulan6">Paket 6 Bulan</SelectItem>
                          <SelectItem value="premium">Paket Premium</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{(u.plan_requested_at || "").slice(0, 10)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button data-testid={`approve-${u.id}`} onClick={() => approve(u.id, overrides[u.id])} disabled={busy === u.id} size="sm" className="gap-1 rounded-full">
                          <Check className="h-3.5 w-3.5" /> Setujui
                        </Button>
                        <Button data-testid={`reject-${u.id}`} onClick={() => reject(u.id)} disabled={busy === u.id} size="sm" variant="outline" className="gap-1 rounded-full text-red-500 hover:text-red-600">
                          <X className="h-3.5 w-3.5" /> Tolak
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        }
      </Card>

      {/* Manual activation for any user */}
      <Card className="p-6" data-testid="manual-activation">
        <p className="mb-4 font-heading text-lg">Aktivasi Manual Semua User</p>
        <p className="mb-4 text-sm text-muted-foreground">Set paket langsung untuk pengguna tanpa perlu mereka mengajukan.</p>
        {loading ? <p className="text-sm text-muted-foreground">Memuat...</p> :
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Username</TableHead>
                  <TableHead>Pasangan</TableHead>
                  <TableHead>Paket Saat Ini</TableHead>
                  <TableHead>Aktif Sampai</TableHead>
                  <TableHead className="text-right">Set Paket</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allUsers.map((u) => (
                  <TableRow key={u.id} data-testid={`manual-row-${u.id}`}>
                    <TableCell className="font-medium">{u.username} <span className="text-xs text-muted-foreground">({u.role})</span></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{u.pasangan_id || "-"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{PLAN_LABELS[u.plan] || u.plan || "-"}</span>
                        <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[u.plan_status] || ""}`}>{u.plan_status || "none"}</Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {u.plan === "premium" ? "Selamanya" : (u.plan_expires_at ? u.plan_expires_at.slice(0, 10) : "-")}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Select value={manualPlan[u.id] || ""} onValueChange={(v) => setManualPlan((p) => ({ ...p, [u.id]: v }))}>
                          <SelectTrigger data-testid={`manual-select-${u.id}`} className="h-8 w-36 rounded-full text-xs">
                            <SelectValue placeholder="Pilih paket" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="bulan1">Paket 1 Bulan</SelectItem>
                            <SelectItem value="bulan6">Paket 6 Bulan</SelectItem>
                            <SelectItem value="premium">Paket Premium</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button data-testid={`manual-activate-${u.id}`} onClick={() => manualActivate(u.id)} disabled={busy === u.id || !manualPlan[u.id]} size="sm" className="rounded-full">
                          Aktifkan
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        }
      </Card>
    </div>
  );
}
